import React, { useCallback, useState, useEffect, useRef, Suspense } from "react";
import SplashLoader from "./components/SplashLoader/SplashLoader";
import "./components/assets/css/LiveChat.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./styles/global.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import APIS from "./common/apis";
import GameRun from "./common/GameRun";
import { fetchLogoOnce } from "./utils/logoService";
import { getCached, setCached, TTL } from "./utils/apiCache";

// ── Lazy-loaded route components (split into separate JS chunks) ──────────────
const Auth               = React.lazy(() => import("./pages/Auth/Auth"));
const Home               = React.lazy(() => import("./pages/Home/Home"));
const ProfileInterface   = React.lazy(() => import("./pages/ProfileInterface/profile-interface"));
const VIPClub            = React.lazy(() => import("./pages/VipClub"));
const AmountEntry        = React.lazy(() => import("./pages/DepositAmount/amount-entry"));
const DocumentsUpload    = React.lazy(() => import("./pages/DocumentsUpload/DocumentsUpload"));
const TransactionProgress= React.lazy(() => import("./pages/TransactionProgress"));
const TransactionRecords = React.lazy(() => import("./pages/TransactionRecords"));
const TransactionDetails = React.lazy(() => import("./pages/TransactionDetails"));
const QuickDeposit       = React.lazy(() => import("./pages/QuickDeposit/QuickDeposit"));
const ReferralProgram    = React.lazy(() => import("./pages/ReferralProgram/ReferralProgram"));
const GamesCasino        = React.lazy(() => import("./pages/GamesCasino"));
const Menu               = React.lazy(() => import("./pages/Menu"));
const Deposit            = React.lazy(() => import("./pages/Deposit"));
const Withdrawal         = React.lazy(() => import("./pages/Withdrawal"));
const Turnover           = React.lazy(() => import("./pages/TurnoverList"));
const Notifications      = React.lazy(() => import("./pages/Notifications"));
const ChangePassword     = React.lazy(() => import("./pages/ChangePassword"));
const ReportProfile      = React.lazy(() => import("./pages/ReportProfile"));
const Promotions         = React.lazy(() => import("./pages/Promotions"));
const PromotionDetail    = React.lazy(() => import("./pages/PromotionDetail"));
const EWallet            = React.lazy(() => import("./pages/EWallet"));
const BrandAmbassadors   = React.lazy(() => import("./components/BrandAmbassadors/BrandAmbassadors"));
const BrandAmbassadorDetail = React.lazy(() => import("./pages/BrandAmbassadorDetail"));
const LocalDepositAmount = React.lazy(() => import("./pages/LocalDepositAmount/amount-entry"));
const MakePayment        = React.lazy(() => import("./pages/MakePayment"));
const BettingRecords     = React.lazy(() => import("./pages/BettingRecords"));
const BettingDetails     = React.lazy(() => import("./pages/BettingDetails"));
const ForgotPassword     = React.lazy(() => import("./pages/ForgotPassword"));
const MyVIP              = React.lazy(() => import("./pages/MyVIP/MyVIP"));
const VIPRebateScreen    = React.lazy(() => import("./pages/VIPRebateScreen/VIPRebateScreen"));
const EXHome             = React.lazy(() => import("./pages/Exchange/EXHome"));
const EXHomeVelki        = React.lazy(() => import("./pages/Exchange/EXHomeVelki"));
const NineWiket          = React.lazy(() => import("./pages/Exchange/NineWiket"));
const ChatHome           = React.lazy(() => import("./components/Chat/ChatHome"));
const ChatHistory        = React.lazy(() => import("./components/Chat/ChatHistory"));
const ChatBox            = React.lazy(() => import("./components/Chat/ChatBox"));import DownloadNotice from './common/downloadNotice';

const MIN_SPLASH_MS = 2000;
const MAX_SPLASH_MS = 8000;

const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);

// ── Route guards defined OUTSIDE component to avoid recreation on every render ──
function PrivateRoute({ isLoggedIn, element }) {
  return isLoggedIn ? element : <Navigate to="/login" />;
}
function PublicRoute({ isLoggedIn, element }) {
  return !isLoggedIn ? element : <Navigate to="/home" replace />;
}

// ── Captures referral / affiliate / agent codes from URL query params ──────
// Persists them to localStorage so Auth.jsx can include them during signup.
// When codes are detected and user is not logged in, redirects to the signup
// page so the account gets created under the correct affiliate.
function AffiliateCodeWatcher() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referral = params.get("referral_code");
    const affiliate = params.get("affiliate_code");
    const agent = params.get("agent_code");

    let captured = false;
    if (referral) {
      localStorage.setItem("referral_code", referral);
      captured = true;
    }
    if (affiliate) {
      localStorage.setItem("affiliate_code", affiliate);
      captured = true;
    }
    if (agent) {
      localStorage.setItem("agent_code", agent);
      captured = true;
    }

    // If we captured a code and user is not logged in, redirect to register
    // (only if not already on the register page to avoid redirect loops)
    if (captured && !localStorage.getItem("token") && location.pathname !== "/register") {
      navigate("/register", { replace: true, state: { from: "reg" } });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // This component renders nothing
}

// ── Landing page for /referral_code links ───────────────────────────────────
// Captures the referral code and redirects to the signup page (or home if
// already logged in, since the code is already stored for future reference).
function ReferralCodeLanding() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referral = params.get("referral_code");
    const affiliate = params.get("affiliate_code");
    const agent = params.get("agent_code");

    if (referral) localStorage.setItem("referral_code", referral);
    if (affiliate) localStorage.setItem("affiliate_code", affiliate);
    if (agent) localStorage.setItem("agent_code", agent);

    const isLoggedIn = !!localStorage.getItem("token");
    if (isLoggedIn) {
      navigate("/home", { replace: true });
    } else {
      navigate("/register", { replace: true, state: { from: "reg" } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function App() {
  const isDeviceMobile = isMobile();
  const [loading, setLoading] = useState(true);
  const splashStart = useRef(performance.now());
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const [showContent, setShowContent] = useState(false);
  const [logo, setLogo] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Splash timing: min 2s, max 8s
  useEffect(() => {
    const link = document.getElementById('app-style');

    const minDelay = new Promise((resolve) => {
      const elapsed = performance.now() - splashStart.current;
      const remaining = Math.max(0, MIN_SPLASH_MS - elapsed);
      setTimeout(resolve, remaining);
    });

    const cssReady = link?.sheet
      ? Promise.resolve()
      : new Promise((resolve) => {
          if (!link) { setTimeout(resolve, 1000); return; }
          const done = () => resolve();
          link.addEventListener('load', done, { once: true });
          link.addEventListener('error', done, { once: true });
          setTimeout(done, MAX_SPLASH_MS);
        });

    Promise.all([cssReady, minDelay]).then(() => setLoading(false));
  }, []);

  // ── /#/9wkt exit handler ──────────────────────────────────────────────────
  // When 9WKT game redirects back with /#/9wkt in the URL, POST exit to backend
  // then strip the hash segment so the app loads cleanly.
  useEffect(() => {
    if (window.location.href.includes("/#/9wkt")) {
      const token = localStorage.getItem("token");
      fetch(`${process.env.REACT_APP_API_URL}/api/9wkt/exit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }).finally(() => {
        const cleanUrl = window.location.href.split("/#/9wkt")[0];
        window.location.replace(cleanUrl);
      });
    }
  }, []);

  // ── Affiliate / Referral code capture from URL ────────────────────────────
  // When a user lands via an affiliate link (e.g. /?referral_code=ABC or
  // /referral_code?referral_code=ABC or /?affiliate_code=XYZ), persist the
  // code(s) in localStorage so Auth.jsx can include them during signup.
  // This runs early (outside Router) to capture codes on initial page load
  // before React hydration. The AffiliateCodeWatcher inside Router handles
  // SPA navigations and performs the redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const referral = params.get("referral_code");
    const affiliate = params.get("affiliate_code");
    const agent = params.get("agent_code");

    if (referral) localStorage.setItem("referral_code", referral);
    if (affiliate) localStorage.setItem("affiliate_code", affiliate);
    if (agent) localStorage.setItem("agent_code", agent);
  }, []);

  const [globalData, setGlobalData] = useState({});
  const [gtmId, setGtmId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [balance, setBalance] = useState(0);
  const [refreshPvtData, setRefreshPvtData] = useState(false);

  // Inject Google Tag Manager script ONCE when gtmId is first available
  useEffect(() => {
    const id = globalData?.general?.google_tag;
    if (!id) return;

    // Only inject once — don't re-inject on subsequent globalData changes
    if (gtmId) return;
    setGtmId(id);

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(script);

    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    return () => {
      const s = document.getElementById("gtm-script");
      if (s) s.remove();
    };
  }, [globalData?.general?.google_tag]); // Only depend on the tag ID, not entire globalData

  // using useEffect on load, get the css from a api and inject it into the document head
  //   useEffect(() => {
  // 	const fetchCss = async () => {
  // 		try {
  // 			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/css`);
  // 			if (response.ok) {
  // 				const cssText = await response.text();
  // 				const styleElement = document.createElement("style");
  // 				styleElement.textContent = cssText;
  // 				document.head.appendChild(styleElement);
  // 			}
  // 		} catch (error) {
  // 			console.error("Error fetching CSS:", error);
  // 		}
  // 	};
  // 	fetchCss();
  // }, []);

  // Function to show alert messages

  const showAlert = (message = false) => {
    if (!message) {
      setShowModal(false);
      return;
    }
    setShowModal(true);
    setModalMessage(message);
  };

  // Function to dynamically set the favicon
  const setFavicon = (url) => {
    if (!url) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  };
  const [cssLoading, setCssLoading] = useState(false);

  // Fetch logo & favicon once — uses singleton (no duplicate network calls)
  useEffect(() => {
    fetchLogoOnce().then(item => {
      if (!item) return;
      setLogo(item.loader_logo || '');
      const faviconPath = item?.logo || item?.favicon;
      if (faviconPath) {
        setFavicon(`${process.env.REACT_APP_API_URL}/storage/${faviconPath}`);
      }
    }).catch(err => console.error("Error fetching logo:", err));
  }, []);

  const [isTitleSet, setIsTitleSet] = useState(false);

  // Fetch general site data — checks sessionStorage cache first to avoid duplicate
  // requests when apis.js has already fetched the same endpoint
  useEffect(() => {
    const fetchGeneral = async () => {
      const cached = getCached('general');
      const d = cached ? (Array.isArray(cached) ? cached[0] : cached) : null;
      if (d) {
        document.title = d.site_title || document.title;
        localStorage.setItem("whatsapp",  d.whatsapp    ?? 'null');
        localStorage.setItem("liveChat",  d.live_chat   ?? 'null');
        localStorage.setItem("intercom",  d.intercom    ?? 'null');
        localStorage.setItem("tawkId",    d?.tawk_id    ?? 'null');
        setIsTitleSet(true);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/general`, { headers });
        if (!response.ok) throw new Error("Failed to fetch General");
        const data = await response.json();
        if (data && data[0]) {
          setCached('general', data, TTL.public);
          document.title = data[0].site_title || document.title;
          localStorage.setItem("whatsapp",  data[0].whatsapp    ?? 'null');
          localStorage.setItem("liveChat",  data[0].live_chat   ?? 'null');
          localStorage.setItem("intercom",  data[0].intercom    ?? 'null');
          localStorage.setItem("tawkId",    data[0]?.tawk_id    ?? 'null');
          setIsTitleSet(true);
        }
      } catch (error) {
        console.error("Error fetching General:", error);
      }
    };
    fetchGeneral();
  }, []);

  // Monitor when both API calls are finished
  useEffect(() => {
    if (logo && isTitleSet) {
      setIsDataLoaded(true);
    }
  }, [logo, isTitleSet]);

  const fetchBalance = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/balance`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const data = await response.json();

          localStorage.setItem("balance", data.balance);
          // Notify all components (Header, etc.) that balance changed
          window.dispatchEvent(new CustomEvent("balance-updated", { detail: data.balance }));
          if (document.querySelector(".mainBalance")) {
            document.querySelector(".mainBalance").innerHTML = data.balance;
          }
        } else if (response.status === 401) {
          handleLogout();
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
      }
    } else {
      console.warn("No token found in local storage.");
    }
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    window.location.href = "/";
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    fetchBalance();
    // Poll every 30s instead of 10s — drastically reduces server load
    let interval = setInterval(fetchBalance, 30000);

    // Pause polling when tab is hidden, resume when visible
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearInterval(interval);
        interval = null;
      } else if (!interval) {
        fetchBalance();
        interval = setInterval(fetchBalance, 30000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isLoggedIn, fetchBalance]);

  return (
    <>
      {loading && <SplashLoader />}
      {gtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          ></iframe>
        </noscript>
      )}
      <Router>
      <DownloadNotice />
      <div className="App">
        <APIS setGlobalData={setGlobalData} refreshPvtData={refreshPvtData} />
        <ToastContainer />
        <AffiliateCodeWatcher />
        <Suspense fallback={<div className="route-fallback" />}>
        <Routes>
          {/* Auth Routes */}
          <Route
            path="/brandAmbassador-detail"
            element={<BrandAmbassadorDetail />}
          />
          <Route path="/brandAmbassadors" element={<BrandAmbassadors />} />
          <Route path="/e-wallet" element={<EWallet />} />
          <Route path="/promotions-detail" element={<PromotionDetail />} />
          <Route
            path="/promotions"
            element={
              <Promotions
                promotions={globalData.promotions?.allPromotions || []}
              />
            }
          />
          <Route path="/report-profile" element={<ReportProfile />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/forget-password" element={<ForgotPassword />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route
            path="/turnover"
            element={<Turnover turnovers={globalData.turnovers || []} />}
          />
          <Route path="/login" element={<PublicRoute isLoggedIn={isLoggedIn} element={<Auth slidersMain={globalData.sliders?.main || []} />} />} />
          <Route
            path="/register"
            element={<PublicRoute isLoggedIn={isLoggedIn} element={<Auth slidersMain={globalData.sliders?.main || []} />} />}
          />
          <Route path="/withdrawal" element={<Withdrawal />} />

          {/* Main Routes */}
          <Route
            path="/home"
            element={
              <Home featuredGames={globalData.sliders?.feature || []} globalData={globalData} userData={globalData.userData} />
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn}
                element={
                  <ProfileInterface
                    fetchBalance={fetchBalance}
                    userData={globalData.userData}
                    general={globalData.general || {}}
                  />
                }
              />
            }
          />
          <Route
            path="/betting-records"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn}
                element={
                  <BettingRecords
                    // fetchBalance={fetchBalance}
                    // userData={globalData.userData}
                    // general={globalData.general || {}}
                  />
                }
              />
            }
          />
          <Route
            path="/betting-details"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn}
                element={
                  <BettingDetails
                    // fetchBalance={fetchBalance}
                    // userData={globalData.userData}
                    // general={globalData.general || {}}
                  />
                }
              />
            }
          />
          <Route path="/menu" element={<Menu sociallink={globalData.socialLinks || {}}   promotions={globalData.promotions?.allPromotions || []} featuredGames={globalData.sliders?.feature || []}/>} />

          <Route
            path="/vip-club"
            element={<PrivateRoute isLoggedIn={isLoggedIn} element={<VIPClub />} />}
          />
          <Route
            path="/vip-point"
            element={<PrivateRoute isLoggedIn={isLoggedIn} element={<MyVIP  userData={globalData.userData} />} />}
          />
          <Route
            path="/vip-rebate"
            element={<PrivateRoute isLoggedIn={isLoggedIn} element={<VIPRebateScreen />} />}
          />
          {/* VIP & Deposits */}
          {/* <Route path="/vip-club" element={<VIPClub />} /> */}
          {/* <Route path="/withdrawal-entry" element={<AmountEntry />} /> */}
          <Route
            path="/withdrawal-entry"
            element={
              <AmountEntry
                general={globalData.general}
                hasEWallet={globalData.hasEWallet}
                userData={globalData.userData || []}
                transactionData={globalData.transactions || []}
                items={globalData.paymentMethods || []}
              />
            }
          />

          {/* <Route path="/local-wallet" element={<LocalDepositAmount />} /> */}
          <Route
            path="/local-wallet"
            element={
              <LocalDepositAmount
                usdtRate={globalData?.general?.usdt_rate}
                userData={globalData.userData || []}
                transactionData={globalData.transactions || []}
                items={globalData.paymentMethods || []}
                promotions={globalData.promotions?.activePromotion || []}
              />
            }
          />
          <Route path="/deposit" element={<Deposit />} />
          <Route
            path="/make-payment"
            element={
              <PrivateRoute
                isLoggedIn={isLoggedIn}
                element={<MakePayment userData={globalData.userData} />}
              />
            }
          />
          <Route
            path="/quick-deposit"
            element={
              <QuickDeposit
                usdtRate={globalData?.general?.usdt_rate}
                hasEWallet={globalData.hasEWallet}
                userData={globalData.userData || []}
                transactionData={globalData.transactions || []}
                items={globalData.paymentMethods || []}
                promotions={globalData.promotions?.activePromotion || []}
              />
            }
          />

          {/* Transactions */}
          <Route
            path="/transactions"
            element={
              <TransactionRecords
                setRefreshPvtData={setRefreshPvtData}
                transactionData={globalData.transactions || []}
              />
            }
          />
          <Route path="/gamerun" element={<GameRun />} />
          <Route path="/exchange" element={<PrivateRoute isLoggedIn={isLoggedIn} element={<EXHome />} />} />
          <Route path="/velki" element={<PrivateRoute isLoggedIn={isLoggedIn} element={<EXHomeVelki />} />} />
          <Route path="/9wiket" element={<NineWiket />} />
          <Route path="/transaction-detail" element={<TransactionDetails />} />
          {/* <Route path="/betting-record" element={<BettingRecord />} /> */}
          <Route
            path="/transaction-progress"
            element={<TransactionProgress />}
          />

          {/* Documents & Referrals */}
          <Route path="/documents" element={<DocumentsUpload />} />
          <Route
            path="/referral"
            element={
              <ReferralProgram
                showAlert={showAlert}
                userData={globalData.userData}
              />
            }
          />
          {/* Referral code landing — capture code from URL and redirect to signup */}
          <Route
            path="/referral_code"
            element={<ReferralCodeLanding />}
          />
          <Route
            path="/affiliate_code"
            element={<ReferralCodeLanding />}
          />
          <Route
            path="/agent_code"
            element={<ReferralCodeLanding />}
          />

          <Route
            path="/casino"
            element={<GamesCasino globalData={globalData} />}
          />
          {/* Live Chat Pages */}
          <Route path="/chat"               element={<PrivateRoute isLoggedIn={isLoggedIn} element={<ChatHome />} />} />
          <Route path="/chat-list"          element={<PrivateRoute isLoggedIn={isLoggedIn} element={<ChatHistory />} />} />
          <Route path="/chat-box/:chatId"   element={<PrivateRoute isLoggedIn={isLoggedIn} element={<ChatBox />} />} />
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/home" replace />} />

          {/* 404 - Not Found */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        </Suspense>
      </div>
    </Router>
    </>
  );
}

export default App;